import { useState, useEffect, useRef } from 'react';
import { Card, Tag, Radio, Divider, Empty, Spin, Button } from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import TabletHeader from '../../../../components/Operations/TabletHeader';
import { useGetInspectionPlansQuery } from '../../../../redux/api/quality';
import { InspectionPlan } from '../../../../interfaces/Quality.Interface';
import { getTagColor } from '../../../../utils/index.ts';
import { useBuildingContext } from '../../../../context/BuildingContext';
import {
	TABLET_TITLE_STYLE,
	TABLET_BACK_BUTTON_STYLE,
	TABLET_SEARCH_STYLE,
	TABLET_RADIO_GROUP_STYLE,
	TABLET_RADIO_BUTTON_STYLE,
	TABLET_CARD_GRID_STYLE,
} from '../../../../components/Operations/tabletStyles';

const { Meta } = Card;

const PAGE_SIZE = 20;

const QCMachine: React.FC = () => {
	const navigate = useNavigate();
	const { buildingId } = useBuildingContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const initialSearchTerm = searchParams.get('search') || '';
	const initialStatus = searchParams.get('status') || 'In Progress';

	const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
	const [status, setStatus] = useState(initialStatus);
	const [page, setPage] = useState(1);
	const [allItems, setAllItems] = useState<InspectionPlan[]>([]);
	const contentRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const param = searchParams.get('search') || '';
		const statusParam = searchParams.get('status') || 'In Progress';
		if (param !== searchTerm) {
			setSearchTerm(param);
		}
		if (statusParam !== status) {
			setStatus(statusParam);
		}
	}, [searchParams]);

	const { data, isLoading, isFetching, isError } = useGetInspectionPlansQuery(
		{
			search: searchTerm,
			queue_status: status,
			queue: 'hand',
			page,
			page_size: PAGE_SIZE,
			...(buildingId ? { building_id: buildingId } : {}),
		},
		{ refetchOnMountOrArgChange: true }
	);

	// Reset pagination when filters change
	useEffect(() => {
		setPage(1);
		setAllItems([]);
	}, [searchTerm, status, buildingId]);

	// Accumulate results across pages; data.previous === null means first page
	useEffect(() => {
		if (!data?.results) return;
		if (!data.previous) {
			setAllItems(data.results);
		} else {
			setAllItems((prev) => [...prev, ...data.results]);
		}
	}, [data]);

	// Auto-load next page if content doesn't overflow, or on scroll near bottom
	useEffect(() => {
		if (!data?.next || isFetching) return;

		const container = contentRef.current;
		if (!container) return;

		// Check after DOM updates whether content overflows
		const rafId = requestAnimationFrame(() => {
			if (container.scrollHeight <= container.clientHeight) {
				setPage((prev) => prev + 1);
			}
		});

		// Content overflows — use scroll listener
		const handleScroll = () => {
			if (!data?.next || isFetching) return;
			const { scrollTop, scrollHeight, clientHeight } = container;
			if (scrollHeight - scrollTop - clientHeight < 300) {
				setPage((prev) => prev + 1);
			}
		};
		container.addEventListener('scroll', handleScroll);
		return () => {
			cancelAnimationFrame(rafId);
			container.removeEventListener('scroll', handleScroll);
		};
	}, [data?.next, isFetching, allItems.length]);

	const handleSearch = (value: string) => {
		setSearchTerm(value);
		setSearchParams(value ? { search: value, status } : { status });
	};

	const handleRefresh = () => {
		setSearchTerm('');
		setPage(1);
		setAllItems([]);
		setSearchParams({ status });
	};

	return (
		<div className="flex flex-col w-full p-3" style={{ height: '100vh' }}>
			<TabletHeader
				title="Hand Quality"
				titleStyle={TABLET_TITLE_STYLE}
				showOnClickButton
				icon={<ArrowLeftOutlined />}
				buttonText="Back"
				actionButtonStyle={TABLET_BACK_BUTTON_STYLE}
				searchStyle={TABLET_SEARCH_STYLE}
				searchPlaceholder="Search Job/Product"
				radioGroupStyle={TABLET_RADIO_GROUP_STYLE}
				handleOnClick={() => navigate('/tablet/operations')}
				onSearch={handleSearch}
				onRadioChange={(e) => {
					const newStatus = e.target.value;
					setStatus(newStatus);
					setSearchParams((prev) => {
						const newParams = new URLSearchParams(prev.toString());
						newParams.set('status', newStatus);
						return newParams;
					});
				}}
				radioOptions={
					<>
						<Radio.Button value="In Progress" style={TABLET_RADIO_BUTTON_STYLE}>
							In Progress
						</Radio.Button>
						<Radio.Button value="Completed" style={TABLET_RADIO_BUTTON_STYLE}>
							Completed
						</Radio.Button>
					</>
				}
				radioValue={status}
			/>

			<Divider style={{ marginBottom: 5 }} />

			<div
				ref={contentRef}
				style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarGutter: 'stable', paddingTop: 8 }}
			>
				{isLoading ? (
					<div style={{ textAlign: 'center', paddingTop: 64 }}>
						<Spin size="large" />
					</div>
				) : isError ? (
					<Empty description="Failed to load data" />
				) : allItems.length === 0 ? (
					<Empty
						description="No current parts in progress"
						style={{ marginTop: 64 }}
					>
						<Button type="primary" onClick={handleRefresh}>
							Refresh
						</Button>
					</Empty>
				) : (
					<div style={TABLET_CARD_GRID_STYLE}>
						{allItems.map((line_item) => {
							const project = line_item.project;
							const orderLineItem = project?.order_line_item;
							const projectId = project?.id;
							const partName = orderLineItem?.name || line_item.name || 'Unknown Part';
							const orderName =
								orderLineItem?.order?.name || orderLineItem?.order_name || 'Unknown Order';
							const dueDate = orderLineItem?.due_date || orderLineItem?.required_partner_ship_date;

							return (
								<Card
									key={line_item.id}
									style={{ backgroundColor: '#f0f7f6', color: '#000' }}
									className="h-full flex flex-col justify-between"
									hoverable={Boolean(projectId)}
									onClick={() => {
										if (projectId) {
											navigate(`${projectId}/inspection-results`);
										}
									}}
									cover={orderLineItem?.thumbnail_file ? (
										<img
											alt={partName}
											className="p-2"
											src={orderLineItem.thumbnail_file}
										/>
									) : undefined}
								>
									<Meta
										title={partName}
										description={
											<div className="flex flex-col gap-1">
												<div>{orderName}</div>
												{dueDate && (
													<div>
														Due Date: {dayjs(dueDate).format('MM-DD-YY')}
													</div>
												)}
											</div>
										}
									/>
									<Tag color={getTagColor(status)} style={{ marginTop: 8 }}>
										{status}
									</Tag>
								</Card>
							);
						})}
						<div ref={sentinelRef} style={{ gridColumn: '1 / -1', height: 1 }}>
							{isFetching && (
								<Spin style={{ display: 'block', margin: '16px auto' }} />
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default QCMachine;
